import React, { useContext, useState } from "react";
import { useParams } from "react-router";
import { Button, Container, Paper, Typography, useMediaQuery, CircularProgress, Alert } from "@mui/material";
import { Link } from "react-router-dom";

import InfoSection, { InformationField } from "../../components/general/InfoSection";

import useResponsive from "../../hooks/useResponsive";
import { Stack } from "@mui/system";
import WorkshopButton from "../../components/workshops/WorkshopButton";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import WorkshopInstanceTable from "../../components/workshops/workshopInstance/WorkshopInstanceTable";

import { useWorkshopDetails } from "../../hooks/workshops/useWorkshopDetails";
import { AuthContext } from "../../context/auth-context/AuthContext";
import AddWorkshopInstanceButton from "../../components/workshops/AddWorkshopInstanceButton";
import AddWorkshopInstanceDialog from "../../components/workshops/AddWorkshopInstanceDialog";
import useMembers from "../../hooks/members/useMembers";

/**
 * Displays the details of a workshop.
 * @returns The details of a workshop and a table of all workshop instances.
 */
const WorkshopDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { auth } = useContext(AuthContext);
  const { permissions } = auth;
  const hasWorkshopPermission = doesPermissionsHaveSomeOf(permissions, [4]);
  const [isAddWorkshopInstanceDialogOpen, setIsAddWorkshopInstanceDialogOpen] = useState(false);

  const isMobile = useResponsive("down", "sm");
  const isSmall = useMediaQuery("(max-width: 1330px)"); // Used for better display on smaller screens

  // Use react-query to fetch workshop details
  const {
    workshopDetails,
    workshopInstances,
    isWorkshopDetailsLoading,
    isWorkshopDetailsError,
    createWorkshopInstance,
  } = useWorkshopDetails(Number(id));

  const { members } = useMembers();

  const membersFieldDto = members.map((member) => ({
    memberId: member.memberId,
    firstname: member.firstname,
    lastname: member.lastname,
  }));

  if (isWorkshopDetailsLoading) {
    return (
      <Container>
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ minHeight: "200px" }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (isWorkshopDetailsError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          Fehler beim Laden des Workshops
        </Alert>
      </Container>
    );
  }

  if (!workshopDetails || !workshopInstances) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Workshop nicht gefunden
        </Alert>
      </Container>
    );
  }

  // Fields to display in the info section
  const displayFields: Array<InformationField> = [
    {
      label: "Workshopname",
      value: workshopDetails ? workshopDetails.workshopName : null,
      type: "text",
    },
    {
      label: "Beschreibung",
      value: workshopDetails ? workshopDetails.description : null,
      type: "text",
    },
    {
      label: "Art",
      value: workshopDetails ? workshopDetails.type : null,
      type: "text",
    },
  ];

  return (
    <Container component={Paper} sx={{ m: 2, p: 2 }}>
      <Stack
        direction={isSmall && !isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        sx={{ mb: 5 }}
        alignItems={isSmall ? "start" : "center"}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"} sx={{ mr: 3 }}>
          {isMobile ? "Details" : "Informationen zum Workshop"}
        </Typography>
        {hasWorkshopPermission ? (
          <Stack direction={"row"} spacing={2}>
            <WorkshopButton
              edit
              workshopId={workshopDetails?.workshopId}
              workshopName={workshopDetails?.workshopName}
              workshopDescription={workshopDetails?.description}
              workshopType={workshopDetails?.type}
            />
            <AddWorkshopInstanceButton setIsAddWorkshopInstanceDialogOpen={setIsAddWorkshopInstanceDialogOpen} />
            <AddWorkshopInstanceDialog
              workshop={workshopDetails}
              isOpen={isAddWorkshopInstanceDialogOpen}
              onClose={() => setIsAddWorkshopInstanceDialogOpen(false)}
              onSave={createWorkshopInstance}
              members={membersFieldDto}
            />
            {workshopInstances.length > 0 ? null : (
              <WorkshopButton
                deletion
                workshopId={workshopDetails?.workshopId}
                workshopName={workshopDetails?.workshopName}
              />
            )}
          </Stack>
        ) : null}
      </Stack>
      <InfoSection fields={displayFields} />
      {workshopInstances.length > 0 && <WorkshopInstanceTable workshopInstances={workshopInstances} />}
      <Button variant="contained" component={Link} to={"/workshops"}>
        Zur Workshopübersicht
      </Button>
    </Container>
  );
};

export default WorkshopDetails;
